import React, { useState, useEffect } from 'react';
import {
  ArrowLeft, User, Briefcase, Calendar, DollarSign, Star, Clock,
  CheckCircle, TrendingUp, Package, X, MapPin, Phone, Mail,
  Edit2, Trash2, AlertCircle, Loader,
} from 'lucide-react';

const API_BASE = 'https://hive-backend-4gcf.onrender.com';

function authHeaders() {
  const token = localStorage.getItem('token');
  return { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };
}

function getStatusColor(status) {
  switch (status) {
    case 'pending': case 'new': case 'open':
      return 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400';
    case 'confirmed': case 'active': case 'in-progress':
      return 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400';
    case 'completed':
      return 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400';
    case 'cancelled': case 'rejected':
      return 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400';
    default:
      return 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-400';
  }
}

function Modal({ title, onClose, children }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="relative w-full max-w-lg bg-white dark:bg-gray-900 rounded-3xl shadow-2xl border border-gray-200/50 dark:border-gray-700/50 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-xl font-black text-gray-900 dark:text-white">{title}</h2>
          <button onClick={onClose} className="p-2 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-800 transition-all">
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>
        <div className="p-6">{children}</div>
      </div>
    </div>
  );
}

function JobDetailModal({ job, onClose, onDelete }) {
  const [deleting, setDeleting] = useState(false);
  const [fullJob, setFullJob] = useState(null);
  const [loadingApplicants, setLoadingApplicants] = useState(true);

  useEffect(() => {
    const fetchFullJob = async () => {
      try {
        const res = await fetch(`${API_BASE}/api/jobs/${job._id}`, { headers: authHeaders() });
        const data = await res.json();
        if (res.ok) setFullJob(data.job || data);
      } catch (err) { console.error(err); }
      finally { setLoadingApplicants(false); }
    };
    fetchFullJob();
  }, [job._id]);

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this job?')) return;
    setDeleting(true);
    try {
      const res = await fetch(`${API_BASE}/api/jobs/${job._id}`, { method: 'DELETE', headers: authHeaders() });
      if (res.ok) { onDelete(job._id); onClose(); }
    } catch (err) { console.error(err); }
    finally { setDeleting(false); }
  };

  const applicants = fullJob?.applications || [];

  return (
    <Modal title="Job Details" onClose={onClose}>
      <div className="space-y-4">
        <div>
          <h3 className="text-2xl font-black text-gray-900 dark:text-white mb-2">{job.title}</h3>
          <span className={`px-3 py-1 rounded-lg text-xs font-bold ${getStatusColor(job.status)}`}>{job.status?.toUpperCase()}</span>
        </div>
        <div className="grid grid-cols-2 gap-3 text-sm">
          <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-3"><p className="text-gray-500 dark:text-gray-400 mb-1">Category</p><p className="font-semibold text-gray-900 dark:text-white">{job.category}</p></div>
          <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-3"><p className="text-gray-500 dark:text-gray-400 mb-1">Budget</p><p className="font-semibold text-gray-900 dark:text-white">LKR {job.budget?.toLocaleString()}</p></div>
          <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-3"><p className="text-gray-500 dark:text-gray-400 mb-1">Urgency</p><p className="font-semibold text-gray-900 dark:text-white capitalize">{job.urgency || 'Normal'}</p></div>
          <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-3"><p className="text-gray-500 dark:text-gray-400 mb-1">Applicants</p><p className="font-semibold text-gray-900 dark:text-white">{applicants.length}</p></div>
        </div>
        {job.location && <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400"><MapPin className="w-4 h-4" /><span className="text-sm">{job.location}</span></div>}
        {job.preferredDate && <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400"><Calendar className="w-4 h-4" /><span className="text-sm">{new Date(job.preferredDate).toLocaleDateString()}{job.preferredTime && ` at ${job.preferredTime}`}</span></div>}
        {job.description && <div><p className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">Description</p><p className="text-sm text-gray-600 dark:text-gray-400 bg-gray-50 dark:bg-gray-800 rounded-xl p-3">{job.description}</p></div>}

        {/* Applicants Section */}
        <div>
          <p className="text-sm font-bold text-gray-700 dark:text-gray-300 mb-3">
            Providers Who Applied ({applicants.length})
          </p>
          {loadingApplicants ? (
            <div className="flex justify-center py-4">
              <div className="w-6 h-6 border-2 border-yellow-400 border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : applicants.length === 0 ? (
            <div className="text-center py-4 bg-gray-50 dark:bg-gray-800 rounded-xl">
              <p className="text-sm text-gray-500 dark:text-gray-400">No one has applied yet.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {applicants.map((app, i) => {
                const provider = app.providerId;
                return (
                  <div key={i} className="bg-gray-50 dark:bg-gray-800 rounded-xl p-4 border border-gray-200 dark:border-gray-700">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-10 h-10 bg-gradient-to-br from-yellow-400 to-yellow-500 rounded-xl flex items-center justify-center text-white font-black">
                        {provider?.name?.charAt(0) || '?'}
                      </div>
                      <div>
                        <p className="font-bold text-gray-900 dark:text-white text-sm">{provider?.name || 'Provider'}</p>
                        {app.quote && <p className="text-xs text-green-600 dark:text-green-400 font-semibold">Quote: LKR {app.quote?.toLocaleString()}</p>}
                      </div>
                    </div>
                    {app.message && (
                      <p className="text-xs text-gray-600 dark:text-gray-400 mb-3 bg-white dark:bg-gray-700 rounded-lg p-2 italic">"{app.message}"</p>
                    )}
                    <div className="flex flex-wrap gap-2">
                      {provider?.email && (
                        <a href={`mailto:${provider.email}`}
                          className="flex items-center gap-1 px-3 py-1.5 bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 rounded-lg text-xs font-semibold hover:bg-blue-100 transition-all">
                          <Mail className="w-3 h-3" /> {provider.email}
                        </a>
                      )}
                      {provider?.phone && (
                        <>
                          <a href={`tel:${provider.phone}`}
                            className="flex items-center gap-1 px-3 py-1.5 bg-yellow-50 dark:bg-yellow-900/20 text-yellow-700 dark:text-yellow-400 rounded-lg text-xs font-semibold hover:bg-yellow-100 transition-all">
                            <Phone className="w-3 h-3" /> Call
                          </a>
                          <a href={`https://wa.me/${provider.phone.replace(/\D/g, '')}`} target="_blank" rel="noopener noreferrer"
                            className="flex items-center gap-1 px-3 py-1.5 bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400 rounded-lg text-xs font-semibold hover:bg-green-100 transition-all">
                            💬 WhatsApp
                          </a>
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <button onClick={handleDelete} disabled={deleting} className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 rounded-xl font-semibold hover:bg-red-100 transition-all text-sm disabled:opacity-50">
          <Trash2 className="w-4 h-4" />{deleting ? 'Deleting...' : 'Delete Job'}
        </button>
      </div>
    </Modal>
  );
}

function BookingDetailModal({ booking, onClose, onCancel }) {
  const [cancelling, setCancelling] = useState(false);
  const handleCancel = async () => {
    if (!window.confirm('Are you sure you want to cancel this booking?')) return;
    setCancelling(true);
    try {
      const res = await fetch(`${API_BASE}/api/bookings/${booking._id}/status`, { method: 'PUT', headers: authHeaders(), body: JSON.stringify({ status: 'cancelled' }) });
      if (res.ok) { onCancel(booking._id); onClose(); }
    } catch (err) { console.error(err); }
    finally { setCancelling(false); }
  };
  const provider = booking.providerId;
  return (
    <Modal title="Booking Details" onClose={onClose}>
      <div className="space-y-4">
        <div>
          <h3 className="text-xl font-black text-gray-900 dark:text-white mb-2">{booking.serviceId?.title || 'Service'}</h3>
          <span className={`px-3 py-1 rounded-lg text-xs font-bold ${getStatusColor(booking.status)}`}>{booking.status?.toUpperCase()}</span>
        </div>
        <div className="grid grid-cols-2 gap-3 text-sm">
          <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-3"><p className="text-gray-500 dark:text-gray-400 mb-1">Date</p><p className="font-semibold text-gray-900 dark:text-white">{booking.bookingDate ? new Date(booking.bookingDate).toLocaleDateString() : 'N/A'}</p></div>
          <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-3"><p className="text-gray-500 dark:text-gray-400 mb-1">Time</p><p className="font-semibold text-gray-900 dark:text-white">{booking.bookingTime || 'N/A'}</p></div>
          <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-3"><p className="text-gray-500 dark:text-gray-400 mb-1">Total Price</p><p className="font-semibold text-gray-900 dark:text-white">LKR {booking.totalPrice?.toLocaleString()}</p></div>
          <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-3"><p className="text-gray-500 dark:text-gray-400 mb-1">Payment</p><p className="font-semibold text-gray-900 dark:text-white capitalize">{booking.paymentStatus || 'Pending'}</p></div>
        </div>
        {provider && <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-4"><p className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Provider</p><p className="font-bold text-gray-900 dark:text-white">{provider.name}</p>{provider.email && <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400 mt-1"><Mail className="w-3 h-3" />{provider.email}</div>}{provider.phone && <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400 mt-1"><Phone className="w-3 h-3" />{provider.phone}</div>}</div>}
        {booking.address && <div className="flex items-start gap-2 text-gray-600 dark:text-gray-400"><MapPin className="w-4 h-4 mt-0.5" /><span className="text-sm">{booking.address}</span></div>}
        {booking.customerNotes && <div><p className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">Notes</p><p className="text-sm text-gray-600 dark:text-gray-400 bg-gray-50 dark:bg-gray-800 rounded-xl p-3">{booking.customerNotes}</p></div>}
        {['pending', 'confirmed'].includes(booking.status) && (
          <button onClick={handleCancel} disabled={cancelling} className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 rounded-xl font-semibold hover:bg-red-100 transition-all text-sm disabled:opacity-50">
            <X className="w-4 h-4" />{cancelling ? 'Cancelling...' : 'Cancel Booking'}
          </button>
        )}
      </div>
    </Modal>
  );
}

function EditServiceModal({ service, onClose, onUpdate }) {
  const [form, setForm] = useState({ title: service.title || '', category: service.category || '', price: service.price || '', priceType: service.priceType || 'hourly', description: service.description || '', location: service.location || '', status: service.status || 'active' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });
  const handleSubmit = async (e) => {
    e.preventDefault(); setSaving(true); setError('');
    try {
      const res = await fetch(`${API_BASE}/api/services/${service._id}`, { method: 'PUT', headers: authHeaders(), body: JSON.stringify(form) });
      const data = await res.json();
      if (res.ok) { onUpdate(data.service); onClose(); }
      else setError(data.message || 'Failed to update service');
    } catch (err) { setError('Failed to connect to server'); }
    finally { setSaving(false); }
  };
  return (
    <Modal title="Edit Service" onClose={onClose}>
      <form onSubmit={handleSubmit} className="space-y-4">
        {error && <div className="flex items-center gap-2 p-3 bg-red-50 dark:bg-red-900/20 rounded-xl text-red-600 dark:text-red-400 text-sm"><AlertCircle className="w-4 h-4" />{error}</div>}
        <div><label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">Title</label><input name="title" value={form.title} onChange={handleChange} required className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl text-gray-900 dark:text-white focus:ring-2 focus:ring-yellow-400 focus:border-transparent" /></div>
        <div className="grid grid-cols-2 gap-3">
          <div><label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">Price (LKR)</label><input name="price" type="number" value={form.price} onChange={handleChange} required className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl text-gray-900 dark:text-white focus:ring-2 focus:ring-yellow-400 focus:border-transparent" /></div>
          <div><label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">Price Type</label><select name="priceType" value={form.priceType} onChange={handleChange} className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl text-gray-900 dark:text-white focus:ring-2 focus:ring-yellow-400 focus:border-transparent"><option value="hourly">Hourly</option><option value="fixed">Fixed</option><option value="daily">Daily</option></select></div>
        </div>
        <div><label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">Location</label><input name="location" value={form.location} onChange={handleChange} className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl text-gray-900 dark:text-white focus:ring-2 focus:ring-yellow-400 focus:border-transparent" /></div>
        <div><label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">Status</label><select name="status" value={form.status} onChange={handleChange} className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl text-gray-900 dark:text-white focus:ring-2 focus:ring-yellow-400 focus:border-transparent"><option value="active">Active</option><option value="inactive">Inactive</option></select></div>
        <div><label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">Description</label><textarea name="description" value={form.description} onChange={handleChange} rows={3} className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl text-gray-900 dark:text-white focus:ring-2 focus:ring-yellow-400 focus:border-transparent resize-none" /></div>
        <div className="flex gap-3 pt-2">
          <button type="button" onClick={onClose} className="flex-1 px-4 py-3 bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 rounded-xl font-semibold hover:bg-gray-200 transition-all text-sm">Cancel</button>
          <button type="submit" disabled={saving} className="flex-1 px-4 py-3 bg-green-500 text-white rounded-xl font-semibold hover:bg-green-600 transition-all text-sm disabled:opacity-50">{saving ? 'Saving...' : 'Save Changes'}</button>
        </div>
      </form>
    </Modal>
  );
}

function ServiceDetailModal({ service, onClose, onDelete }) {
  const [deleting, setDeleting] = useState(false);
  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this service?')) return;
    setDeleting(true);
    try {
      const res = await fetch(`${API_BASE}/api/services/${service._id}`, { method: 'DELETE', headers: authHeaders() });
      if (res.ok) { onDelete(service._id); onClose(); }
    } catch (err) { console.error(err); }
    finally { setDeleting(false); }
  };
  return (
    <Modal title="Service Details" onClose={onClose}>
      <div className="space-y-4">
        <div>
          <h3 className="text-xl font-black text-gray-900 dark:text-white mb-2">{service.title}</h3>
          <span className={`px-3 py-1 rounded-lg text-xs font-bold ${getStatusColor(service.status)}`}>{service.status?.toUpperCase()}</span>
        </div>
        <div className="grid grid-cols-2 gap-3 text-sm">
          <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-3"><p className="text-gray-500 dark:text-gray-400 mb-1">Price</p><p className="font-semibold text-gray-900 dark:text-white">LKR {service.price?.toLocaleString()}/{service.priceType}</p></div>
          <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-3"><p className="text-gray-500 dark:text-gray-400 mb-1">Category</p><p className="font-semibold text-gray-900 dark:text-white">{service.category}</p></div>
          {service.averageRating > 0 && <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-3"><p className="text-gray-500 dark:text-gray-400 mb-1">Rating</p><div className="flex items-center gap-1"><Star className="w-4 h-4 text-yellow-500 fill-yellow-500" /><p className="font-semibold text-gray-900 dark:text-white">{service.averageRating}</p></div></div>}
          <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-3"><p className="text-gray-500 dark:text-gray-400 mb-1">Total Bookings</p><p className="font-semibold text-gray-900 dark:text-white">{service.totalBookings || 0}</p></div>
        </div>
        {service.location && <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400"><MapPin className="w-4 h-4" /><span className="text-sm">{service.location}</span></div>}
        {service.description && <div><p className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">Description</p><p className="text-sm text-gray-600 dark:text-gray-400 bg-gray-50 dark:bg-gray-800 rounded-xl p-3">{service.description}</p></div>}
        <button onClick={handleDelete} disabled={deleting} className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 rounded-xl font-semibold hover:bg-red-100 transition-all text-sm disabled:opacity-50">
          <Trash2 className="w-4 h-4" />{deleting ? 'Deleting...' : 'Delete Service'}
        </button>
      </div>
    </Modal>
  );
}

export default function Dashboard() {
  const [activeView, setActiveView] = useState('customer');
  const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
  const user = { name: storedUser.name || 'User', role: storedUser.userType || 'both' };

  const [myJobs, setMyJobs] = useState([]);
  const [myBookings, setMyBookings] = useState([]);
  const [myServices, setMyServices] = useState([]);
  const [customerStats, setCustomerStats] = useState({ totalJobsPosted: 0, activeBookings: 0, completedBookings: 0, totalSpent: 0 });
  const [providerStats, setProviderStats] = useState({ totalServices: 0, activeJobs: 0, completedJobs: 0, totalEarnings: 0, averageRating: 0 });
  const [loading, setLoading] = useState(true);

  const [selectedJob, setSelectedJob] = useState(null);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [selectedService, setSelectedService] = useState(null);
  const [editingService, setEditingService] = useState(null);

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    setLoading(true);
    try { await Promise.all([fetchJobs(), fetchBookings(), fetchServices()]); }
    finally { setLoading(false); }
  };

  const fetchJobs = async () => {
    try {
      const res = await fetch(`${API_BASE}/api/jobs/user/my-jobs`, { headers: authHeaders() });
      const data = await res.json();
      if (data.success) setMyJobs(data.jobs || []);
    } catch (err) { console.error(err); }
  };

  const fetchBookings = async () => {
    try {
      const res = await fetch(`${API_BASE}/api/bookings`, { headers: authHeaders() });
      const data = await res.json();
      if (data.success) {
        const bookings = data.bookings || [];
        setMyBookings(bookings);
        const active = bookings.filter(b => ['pending', 'confirmed', 'in-progress'].includes(b.status)).length;
        const completed = bookings.filter(b => b.status === 'completed').length;
        const spent = bookings.filter(b => b.status === 'completed').reduce((sum, b) => sum + (b.totalPrice || 0), 0);
        const earned = bookings.filter(b => b.status === 'completed' && b.providerId?._id === storedUser._id).reduce((sum, b) => sum + (b.totalPrice || 0), 0);
        setCustomerStats(prev => ({ ...prev, activeBookings: active, completedBookings: completed, totalSpent: spent }));
        setProviderStats(prev => ({ ...prev, completedJobs: completed, totalEarnings: earned }));
      }
    } catch (err) { console.error(err); }
  };

  const fetchServices = async () => {
    try {
      const res = await fetch(`${API_BASE}/api/services/user/my-services`, { headers: authHeaders() });
      const data = await res.json();
      if (data.success) {
        const services = data.services || [];
        setMyServices(services);
        const avgRating = services.length ? (services.reduce((sum, s) => sum + (s.averageRating || 0), 0) / services.length).toFixed(2) : 0;
        setProviderStats(prev => ({ ...prev, totalServices: services.length, averageRating: parseFloat(avgRating) }));
      }
    } catch (err) { console.error(err); }
  };

  useEffect(() => {
    setCustomerStats(prev => ({ ...prev, totalJobsPosted: myJobs.length }));
    setProviderStats(prev => ({ ...prev, activeJobs: myJobs.filter(j => j.status === 'open').length }));
  }, [myJobs]);

  const activeBookings = myBookings.filter(b => ['pending', 'confirmed', 'in-progress'].includes(b.status));

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white dark:from-gray-900 dark:to-gray-800 transition-colors duration-300 py-6 px-3 sm:px-4">

      {selectedJob && <JobDetailModal job={selectedJob} onClose={() => setSelectedJob(null)} onDelete={(id) => setMyJobs(prev => prev.filter(j => j._id !== id))} />}
      {selectedBooking && <BookingDetailModal booking={selectedBooking} onClose={() => setSelectedBooking(null)} onCancel={(id) => setMyBookings(prev => prev.map(b => b._id === id ? { ...b, status: 'cancelled' } : b))} />}
      {selectedService && <ServiceDetailModal service={selectedService} onClose={() => setSelectedService(null)} onDelete={(id) => setMyServices(prev => prev.filter(s => s._id !== id))} />}
      {editingService && <EditServiceModal service={editingService} onClose={() => setEditingService(null)} onUpdate={(updated) => setMyServices(prev => prev.map(s => s._id === updated._id ? updated : s))} />}

      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 -left-20 w-96 h-96 bg-yellow-400/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 -right-20 w-96 h-96 bg-green-400/10 rounded-full blur-3xl"></div>
      </div>

      <div className="relative max-w-7xl mx-auto">

        {/* Header */}
        <div className="mb-6">
          <button onClick={() => window.location.href = '/'} className="mb-3 flex items-center space-x-2 text-gray-600 dark:text-gray-400 hover:text-yellow-600 dark:hover:text-yellow-400 transition-colors">
            <ArrowLeft className="w-5 h-5" />
            <span className="font-medium text-sm">Back to Home</span>
          </button>
          <div className="bg-white/80 dark:bg-gray-900/80 backdrop-blur-xl rounded-2xl shadow-xl border border-gray-200/50 dark:border-gray-700/50 p-4 sm:p-6">
            <h1 className="text-xl sm:text-3xl font-black text-gray-900 dark:text-white mb-1">
              Welcome back, {user.name}! 👋
            </h1>
            <p className="text-sm text-gray-600 dark:text-gray-400">Manage your jobs, services, and bookings all in one place</p>
          </div>
        </div>

        {/* Toggle Tabs - Mobile optimized */}
        <div className="bg-white/80 dark:bg-gray-900/80 backdrop-blur-xl rounded-2xl shadow-xl border border-gray-200/50 dark:border-gray-700/50 p-1.5 mb-5">
          <div className="flex items-center gap-1.5">
            <button onClick={() => setActiveView('customer')}
              className={`flex-1 flex items-center justify-center gap-1.5 py-3 rounded-xl font-bold text-sm sm:text-base transition-all ${
                activeView === 'customer'
                  ? 'bg-gradient-to-r from-yellow-400 to-yellow-500 text-white shadow-lg'
                  : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800'
              }`}
            >
              <User className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" />
              <span>Customer</span>
            </button>
            <button onClick={() => setActiveView('provider')}
              className={`flex-1 flex items-center justify-center gap-1.5 py-3 rounded-xl font-bold text-sm sm:text-base transition-all ${
                activeView === 'provider'
                  ? 'bg-gradient-to-r from-green-400 to-green-500 text-white shadow-lg'
                  : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800'
              }`}
            >
              <Briefcase className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" />
              <span>Provider</span>
            </button>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-10 h-10 border-4 border-yellow-400 border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : activeView === 'customer' ? (

          <div className="space-y-5">
            {/* Stats - 2x2 grid on mobile, 4 cols on desktop */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {[
                { icon: <Package className="w-6 h-6 sm:w-8 sm:h-8 text-yellow-500" />, value: customerStats.totalJobsPosted, label: 'Jobs Posted' },
                { icon: <Clock className="w-6 h-6 sm:w-8 sm:h-8 text-blue-500" />, value: customerStats.activeBookings, label: 'Active Bookings' },
                { icon: <CheckCircle className="w-6 h-6 sm:w-8 sm:h-8 text-green-500" />, value: customerStats.completedBookings, label: 'Completed' },
                { icon: <DollarSign className="w-6 h-6 sm:w-8 sm:h-8 text-purple-500" />, value: `LKR ${customerStats.totalSpent.toLocaleString()}`, label: 'Total Spent' },
              ].map((stat, i) => (
                <div key={i} className="bg-white/80 dark:bg-gray-900/80 backdrop-blur-xl rounded-2xl shadow-lg border border-gray-200/50 dark:border-gray-700/50 p-4">
                  <div className="flex items-center justify-between mb-2">
                    {stat.icon}
                    <span className="text-lg sm:text-2xl font-black text-gray-900 dark:text-white">{stat.value}</span>
                  </div>
                  <p className="text-xs sm:text-sm font-semibold text-gray-600 dark:text-gray-400">{stat.label}</p>
                </div>
              ))}
            </div>

            {/* Posted Jobs */}
            <div className="bg-white/80 dark:bg-gray-900/80 backdrop-blur-xl rounded-2xl shadow-xl border border-gray-200/50 dark:border-gray-700/50 p-4 sm:p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg sm:text-2xl font-black text-gray-900 dark:text-white">My Posted Jobs</h2>
                <button onClick={() => window.location.href = '/post-job'}
                  className="px-3 py-1.5 sm:px-4 sm:py-2 bg-yellow-500 text-white rounded-xl font-semibold hover:bg-yellow-600 transition-all text-sm"
                >+ New Job</button>
              </div>
              {myJobs.length === 0 ? (
                <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                  <Package className="w-10 h-10 mx-auto mb-3 opacity-30" />
                  <p className="font-semibold text-sm">No jobs posted yet</p>
                  <p className="text-xs mt-1">Post a job to get started</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {myJobs.map((job) => (
                    <div key={job._id} className="bg-gray-50 dark:bg-gray-800/50 rounded-xl p-4 border border-gray-200 dark:border-gray-700">
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex-1 mr-2">
                          <h3 className="text-sm sm:text-base font-bold text-gray-900 dark:text-white mb-0.5 leading-tight">{job.title}</h3>
                          <p className="text-xs text-gray-600 dark:text-gray-400">
                            {job.category} • {job.preferredDate ? new Date(job.preferredDate).toLocaleDateString() : 'No date'}
                          </p>
                        </div>
                        <span className={`px-2 py-0.5 rounded-lg text-xs font-bold flex-shrink-0 ${getStatusColor(job.status)}`}>
                          {job.status?.toUpperCase()}
                        </span>
                      </div>
                      <div className="flex items-center justify-between mt-3">
                        <div className="text-xs text-gray-600 dark:text-gray-400">
                          <span className="font-semibold text-gray-900 dark:text-white">LKR {job.budget?.toLocaleString()}</span>
                          <span className="ml-2">{job.applications?.length || 0} applicants</span>
                        </div>
                        <div className="flex gap-2">
                          <button onClick={() => window.location.href = `/edit-job/${job._id}`}
                            className="px-3 py-1.5 bg-yellow-50 dark:bg-yellow-900/20 text-yellow-700 dark:text-yellow-400 rounded-lg font-semibold text-xs hover:shadow-md transition-all"
                          >Edit</button>
                          <button onClick={() => setSelectedJob(job)}
                            className="px-3 py-1.5 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg font-semibold text-xs hover:shadow-md transition-all"
                          >View</button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Active Bookings */}
            <div className="bg-white/80 dark:bg-gray-900/80 backdrop-blur-xl rounded-2xl shadow-xl border border-gray-200/50 dark:border-gray-700/50 p-4 sm:p-6">
              <h2 className="text-lg sm:text-2xl font-black text-gray-900 dark:text-white mb-4">Active Bookings</h2>
              {activeBookings.length === 0 ? (
                <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                  <Clock className="w-10 h-10 mx-auto mb-3 opacity-30" />
                  <p className="font-semibold text-sm">No active bookings</p>
                  <p className="text-xs mt-1">Book a service to get started</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {activeBookings.map((booking) => (
                    <div key={booking._id} className="bg-gray-50 dark:bg-gray-800/50 rounded-xl p-4 border border-gray-200 dark:border-gray-700">
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex-1 mr-2">
                          <h3 className="text-sm font-bold text-gray-900 dark:text-white mb-0.5">{booking.serviceId?.title || 'Service'}</h3>
                          <p className="text-xs text-gray-600 dark:text-gray-400">Provider: {booking.providerId?.name || 'N/A'}</p>
                        </div>
                        <span className={`px-2 py-0.5 rounded-lg text-xs font-bold flex-shrink-0 ${getStatusColor(booking.status)}`}>
                          {booking.status?.toUpperCase()}
                        </span>
                      </div>
                      <div className="flex items-center justify-between mt-3">
                        <div className="text-xs text-gray-600 dark:text-gray-400">
                          <span>{booking.bookingDate ? new Date(booking.bookingDate).toLocaleDateString() : 'N/A'}</span>
                          <span className="ml-2 font-semibold text-gray-900 dark:text-white">LKR {booking.totalPrice?.toLocaleString()}</span>
                        </div>
                        <button onClick={() => setSelectedBooking(booking)}
                          className="px-3 py-1.5 bg-yellow-500 text-white rounded-lg font-semibold text-xs hover:bg-yellow-600 transition-all"
                        >View</button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

        ) : (

          <div className="space-y-5">
            {/* Provider Stats - 2x3 grid on mobile */}
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
              {[
                { icon: <Briefcase className="w-6 h-6 sm:w-8 sm:h-8 text-green-500" />, value: providerStats.totalServices, label: 'Services' },
                { icon: <Clock className="w-6 h-6 sm:w-8 sm:h-8 text-blue-500" />, value: providerStats.activeJobs, label: 'Active Jobs' },
                { icon: <CheckCircle className="w-6 h-6 sm:w-8 sm:h-8 text-purple-500" />, value: providerStats.completedJobs, label: 'Completed' },
                { icon: <TrendingUp className="w-6 h-6 sm:w-8 sm:h-8 text-yellow-500" />, value: `LKR ${providerStats.totalEarnings.toLocaleString()}`, label: 'Earnings' },
                { icon: <Star className="w-6 h-6 sm:w-8 sm:h-8 text-orange-500" />, value: providerStats.averageRating || '—', label: 'Avg Rating' },
              ].map((stat, i) => (
                <div key={i} className="bg-white/80 dark:bg-gray-900/80 backdrop-blur-xl rounded-2xl shadow-lg border border-gray-200/50 dark:border-gray-700/50 p-4">
                  <div className="flex items-center justify-between mb-2">
                    {stat.icon}
                    <span className="text-lg sm:text-2xl font-black text-gray-900 dark:text-white">{stat.value}</span>
                  </div>
                  <p className="text-xs sm:text-sm font-semibold text-gray-600 dark:text-gray-400">{stat.label}</p>
                </div>
              ))}
            </div>

            {/* My Services */}
            <div className="bg-white/80 dark:bg-gray-900/80 backdrop-blur-xl rounded-2xl shadow-xl border border-gray-200/50 dark:border-gray-700/50 p-4 sm:p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg sm:text-2xl font-black text-gray-900 dark:text-white">My Services</h2>
                <button onClick={() => window.location.href = '/post-service'}
                  className="px-3 py-1.5 sm:px-4 sm:py-2 bg-green-500 text-white rounded-xl font-semibold hover:bg-green-600 transition-all text-sm"
                >+ New Service</button>
              </div>
              {myServices.length === 0 ? (
                <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                  <Briefcase className="w-10 h-10 mx-auto mb-3 opacity-30" />
                  <p className="font-semibold text-sm">No services listed yet</p>
                  <p className="text-xs mt-1">Add a service to start earning</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {myServices.map((service) => (
                    <div key={service._id} className="bg-gray-50 dark:bg-gray-800/50 rounded-xl p-4 border border-gray-200 dark:border-gray-700">
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex-1 mr-2">
                          <h3 className="text-sm font-bold text-gray-900 dark:text-white mb-0.5 leading-tight">{service.title}</h3>
                          <p className="text-xs text-gray-600 dark:text-gray-400">{service.category}</p>
                        </div>
                        <span className={`px-2 py-0.5 rounded-lg text-xs font-bold flex-shrink-0 ${getStatusColor(service.status)}`}>
                          {service.status?.toUpperCase()}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 mb-3">
                        <span className="text-xs font-bold text-gray-900 dark:text-white">LKR {service.price?.toLocaleString()}/{service.priceType}</span>
                        {service.averageRating > 0 && (
                          <div className="flex items-center gap-0.5">
                            <Star className="w-3 h-3 text-yellow-500 fill-yellow-500" />
                            <span className="text-xs font-semibold text-gray-900 dark:text-white">{service.averageRating}</span>
                          </div>
                        )}
                      </div>
                      <div className="flex gap-2">
                        <button onClick={() => setEditingService(service)}
                          className="flex-1 flex items-center justify-center gap-1 px-3 py-1.5 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg font-semibold text-xs hover:shadow-md transition-all"
                        ><Edit2 className="w-3 h-3" /> Edit</button>
                        <button onClick={() => setSelectedService(service)}
                          className="flex-1 px-3 py-1.5 bg-green-500 text-white rounded-lg font-semibold text-xs hover:bg-green-600 transition-all"
                        >View</button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}